import React, {useState} from "react";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {useAuth} from "hooks/useAuth";
import {useServerErrors} from "hooks/useServerErrors";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import CustomButton from "shared/ui/CustomButton";
import CheckBoxField from "shared/form/CheckBoxField";
import {APIPostContactForm} from "../api/publicPages";

interface IFormRegister {
    studentNumberInterval: string;
    studentNumber: string;
    last_name: string;
    first_name: string;
    email: string;
    phone: string;
    schoolName: string;
    city: string;
    county: string;
    message: string;
}

const schema = yup
    .object({})
    .required();

const SELECT_OPTIONS = [
    { label: "maxim 100 elevi", value: "100" },
    { label: "101-250 elevi", value: "101" },
    { label: "251-500 elevi", value: "251" },
    { label: "peste 500 elevi", value: "500" },
];

const ContactForm = ({type, advanced = false} : {type: string, advanced?: boolean}) => {
    const {update} = useAuth();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [gdprConfirm, setGdprConfirm] = useState<boolean>(false);
    const [schoolConfirm, setSchoolConfirm] = useState<boolean>(false);

    const methods = useForm<IFormRegister>({
        resolver: yupResolver(schema),
    });

    const { handleServerErrors } = useServerErrors<IFormRegister>(methods.setError);

    const handleGdprConfirm = () => setGdprConfirm((prev) => !prev);
    const handleSchoolConfirm = () => setSchoolConfirm((prev) => !prev);

    const showSuccessNotification = () => {
        update("error", {
            show: true,
            message: "Un reprezentant Brio îți va răspunde în cel mai scurt timp posibil.",
            title: "Mesajul a fost trimis cu succes",
            icon: "fat envelope",
            closable: true,
            button: "ÎNCHIDE",
        });
    };

    const resetForm = () => {
        methods.reset({
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            schoolName: "",
            city: "",
            county: "",
            message: "",
        });
        setGdprConfirm(false);
        setSchoolConfirm(false);
        setErrorMessage("");
    }

    const onSubmit: SubmitHandler<IFormRegister> = (data) => {
        APIPostContactForm(type, {...data})
            .then((response) => {
                if (response.data.success) {
                    // console.log("postRegister OK", response);
                    showSuccessNotification();
                    resetForm();
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                // console.log("postRegister ERR", err);
                if (err?.response?.status === 400) {
                    handleServerErrors(err.response.data.data);
                } else {
                    update("error", {show: true, code: "A001"});
                }
            })
            .finally(() => {
                update("loading", {show: false});
            });
    };

    return (
        <Box width="100%">
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                    <Grid container spacing={1.5} alignItems="center">
                        {advanced && (
                            <>
                                <FormLabelBox />
                                <FormFieldBox>
                                    <Typography variant="h2" mt={2}>Detalii pachet</Typography>
                                    <Typography variant="body1" fontWeight="bold">Pachet educațional FULL, Ciclu Gimnazial, 36 luni</Typography>
                                </FormFieldBox>
                                <FormLabelBox title="Alege o opțiune:"/>
                                <FormFieldBox>
                                    <FormField
                                        name="studentNumberInterval"
                                        type="select"
                                        label="Alege o opțiune"
                                        defaultValue=""
                                        options={SELECT_OPTIONS}
                                        errorMessage={methods.formState.errors.studentNumberInterval?.message}
                                        error={!!methods.formState.errors.studentNumberInterval?.message}
                                    />
                                </FormFieldBox>
                                <FormLabelBox title="Numar elevi:"/>
                                <FormFieldBox>
                                    <FormField
                                        name="studentNumber"
                                        type="text"
                                        defaultValue=""
                                        label="Suplimentare acces, numar elevi"
                                        autoFocus={true}
                                        error={!!methods.formState.errors.studentNumber?.message}
                                        errorMessage={methods.formState.errors.studentNumber?.message}
                                    />
                                </FormFieldBox>
                                <FormLabelBox />
                                <FormFieldBox>
                                    <Typography variant="h2" mt={2}>Detalii contact</Typography>
                                </FormFieldBox>
                            </>
                        )}
                        <FormLabelBox title="Prenume:"/>
                        <FormFieldBox>
                            <FormField
                                name="first_name"
                                type="text"
                                defaultValue=""
                                label="Prenume"
                                autoFocus={true}
                                error={!!methods.formState.errors.first_name?.message}
                                errorMessage={methods.formState.errors.first_name?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Nume:"/>
                        <FormFieldBox>
                            <FormField
                                name="last_name"
                                type="text"
                                defaultValue=""
                                label="Nume"
                                error={!!methods.formState.errors.last_name?.message}
                                errorMessage={methods.formState.errors.last_name?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Email:"/>
                        <FormFieldBox>
                            <FormField
                                name="email"
                                type="text"
                                defaultValue=""
                                label="Adresa de e-mail"
                                autoComplete="email"
                                error={!!methods.formState.errors.email?.message}
                                errorMessage={methods.formState.errors.email?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Telefon:"/>
                        <FormFieldBox>
                            <FormField
                                name="phone"
                                type="text"
                                label="Telefon"
                                defaultValue=""
                                errorMessage={methods.formState.errors.phone?.message}
                                error={!!methods.formState.errors.phone?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Numele școlii:"/>
                        <FormFieldBox>
                            <FormField
                                name="schoolName"
                                type="text"
                                label="Numele școlii"
                                defaultValue=""
                                errorMessage={methods.formState.errors.schoolName?.message}
                                error={!!methods.formState.errors.schoolName?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Oraș:"/>
                        <FormFieldBox>
                            <FormField
                                name="city"
                                type="text"
                                label="Oraș"
                                defaultValue=""
                                errorMessage={methods.formState.errors.city?.message}
                                error={!!methods.formState.errors.city?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Județ:"/>
                        <FormFieldBox>
                            <FormField
                                name="county"
                                type="text"
                                label="Județ"
                                defaultValue=""
                                errorMessage={methods.formState.errors.county?.message}
                                error={!!methods.formState.errors.county?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox title="Mesaj:"/>
                        <FormFieldBox>
                            <FormField
                                name="message"
                                type="textarea"
                                defaultValue=""
                                label="Mesaj"
                                rows={4}
                                errorMessage={methods.formState.errors.message?.message}
                                error={!!methods.formState.errors.message?.message}
                            />
                        </FormFieldBox>
                        <FormLabelBox/>
                        <FormFieldBox>
                            <CheckBoxField
                                checked={gdprConfirm}
                                name="gdprConfirm"
                                type="checkbox"
                                label="Confirm acordul explicit ca datele mele personale să fie prelucrate de către Brio
                                 Teste Educaționale S.A., cu sediul și adresa de contact în Str. Icoanei Nr. 29A,
                                 București, pentru înscrierea în echipa profesorilor Brio și în scop de marketing direct."
                                labelPlacement="end"
                                onChange={handleGdprConfirm}
                            />
                        </FormFieldBox>
                        <FormLabelBox/>
                        <FormFieldBox>
                            <CheckBoxField
                                checked={schoolConfirm}
                                name="schoolConfirm"
                                type="checkbox"
                                label={
                                <>
                                    Confirm că reprezint o școală eligibilă.
                                    {advanced && (
                                        <>
                                            {" "}
                                            Verifică lista
                                            {" "}
                                            <a href="https://www.edu.ro/sites/default/files/_fi%C8%99iere/Minister/2023/PNRR/PNRAS_II/documente_finale/Lista_scoli_eligibile_PNRAS2.pdf" target="_blank" rel="noreferrer">
                                                aici.
                                            </a>
                                        </>
                                    )}
                                </>}
                                labelPlacement="end"
                                onChange={handleSchoolConfirm}
                            />
                            <Typography variant="body1">Am fost înștiințat că îmi pot exercita gratuit dreptul de
                                informare, de acces, opoziție, intervenție, de a nu fi supus unei decizii individuale,
                                de a depune o plângere în fața autorității de supraveghere competentă, dreptul de a mă
                                adresa justiției și/sau orice alte drepturi prevăzute de legislația în vigoare printr-o
                                cerere scrisă, datată și semnată trimisă la adresa Str. Icoanei Nr. 29A, București. Mai
                                multe informații privind politica noastră de protecție a datelor dvs. personale găsiți
                                pe site-ul nostru în secțiunea ”Confidențialitatea datelor” a site-ului.
                            </Typography>
                        </FormFieldBox>
                        <FormLabelBox/>
                        <FormFieldBox>
                            <CustomButton buttonType="textIcon" bgcolor="dark" submit={true}
                                          disabled={!gdprConfirm || !schoolConfirm}>
                                {advanced ? "TRIMITE SOLICITAREA" : "TRIMITE MESAJUL"}
                            </CustomButton>
                        </FormFieldBox>
                        <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                    </Grid>
                </form>
            </FormProvider>
        </Box>
    );
};

export default ContactForm;
